// GlobalSwitch.tsx
import React from 'react';

interface GlobalSwitchProps {
  label?: string;
  value: boolean;
  onChange: (value: boolean) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  labelPosition?: 'left' | 'right' | 'top';
}

export const GlobalSwitch: React.FC<GlobalSwitchProps> = ({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  error = '',
  helperText = '',
  labelPosition = 'right',
}) => {
  const switchElement = (
    <div
      className={`global-switch ${value ? 'checked' : ''} ${disabled ? 'disabled' : ''}`}
      onClick={() => !disabled && onChange(!value)}
      role="switch"
      aria-checked={value}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
          e.preventDefault();
          onChange(!value);
        }
      }}
    >
      <div className="switch-slider">
        <div className="switch-handle"></div>
      </div>
    </div>
  );

  const labelElement = label && (
    <label
      style={{
        fontSize: '14px',
        fontWeight: 500,
        color: error ? '#ef4444' : '#1e3a8a',
        cursor: disabled ? 'not-allowed' : 'pointer',
        userSelect: 'none',
      }}
      onClick={() => !disabled && onChange(!value)}
    >
      {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
    </label>
  );

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: labelPosition === 'top' ? 'column' : 'row',
          alignItems: labelPosition === 'top' ? 'flex-start' : 'center',
          gap: labelPosition === 'top' ? '8px' : '12px',
        }}
      >
        {labelPosition === 'left' && labelElement}
        {switchElement}
        {(labelPosition === 'right' || labelPosition === 'top') && labelElement}
      </div>

      {helperText && !error && (
        <span
          style={{
            display: 'block',
            marginTop: '6px',
            fontSize: '12px',
            color: '#64748b',
            fontWeight: 400,
          }}
        >
          {helperText}
        </span>
      )}

      {error && (
        <span
          style={{
            display: 'block',
            marginTop: '6px',
            fontSize: '12px',
            color: '#ef4444',
            fontWeight: 500,
          }}
        >
          {error}
        </span>
      )}

      <style>{`
        .global-switch {
          position: relative;
          width: 2.4rem;
          height: 1.2rem;
          display: inline-block;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .global-switch.disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .global-switch:focus-visible {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
          border-radius: 1.2rem;
        }

        .switch-slider {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #cbd5e1;
          border-radius: 1.2rem;
          transition: background-color 0.2s ease;
        }

        .global-switch:hover:not(.disabled) .switch-slider {
          background-color: #94a3b8;
        }

        .global-switch.checked .switch-slider {
          background-color: #3b82f6;
        }

        .global-switch.checked:hover:not(.disabled) .switch-slider {
          background-color: #2563eb;
        }

        .switch-handle {
          position: absolute;
          width: 0.8rem;
          height: 0.8rem;
          left: 0.2rem;
          top: 50%;
          transform: translateY(-50%);
          background-color: white;
          border-radius: 50%;
          transition: transform 0.2s ease;
        }

        .global-switch.checked .switch-handle {
          transform: translate(1rem, -50%);
        }
      `}</style>
    </div>
  );
};